import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { QuickStockAdapterImpl } from '../../core/adapters/quick-stock.adapter';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil, take, finalize } from 'rxjs/operators';
import CategoryHierarchy = Occ.CategoryHierarchy;
import {GlobalMessageService, GlobalMessageType, Occ} from "@spartacus/core";
import { LaunchDialogService } from '@spartacus/storefront';
import {
  Grade,
  ProductQuickStockData,
  QuickStockData,
  QuickStockDataTubes,
  TableRow,
} from '../../core/interfaces/GonvarriQuickStock';
import { GonvarriCartUiEventAddToCart } from 'src/app/core/events/GonvarriCartUiEventAddToCart';
import {isAsturias, isPortugal} from "../../utils/check-environment";
import {GonvarriProduct} from "../../core/models/gonvarri-product.model";
import {GonvarriQuickStockNotificationEvent} from "../../core/events/gonvarri-quick-stock-notification-event";

interface AddToCartDialogData {
  thickness: string;
  width: string;
  length: string;
  stockType: string;
  stockValue: number;
}

type StockType = 'bobinas' | '2.0' | '2.5' | '3.0';

interface StockTypeConfig {
  value: number;
  label: string;
}

@Component({
    selector: 'app-quick-stock',
    templateUrl: 'gonvarri-quick-stock.component.html',
    styleUrls: ['gonvarri-quick-stock.component.scss'],
    standalone: false
})
export class GonvarriQuickStockComponent implements OnInit, OnDestroy {

  formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", currencyDisplay: "code" });

   readonly destroy$ = new Subject<void>();
   readonly ASTURIAS_DEFAULT_CODE = '3030002';
   readonly ASTURIAS_DEFAULT_GROUP = 'Galvanized';

  quickStockData = new BehaviorSubject<CategoryHierarchy | null>(null);
  tableData: TableRow[] = [];
  tableDataTubes: QuickStockDataTubes = { productList: [] };

  subcategories: Array<any> = [];

  selectedCode = '';
  selectedGroup = '';
  selectedCategory?: string;
  selectedSubCategory?: string;
  loading = false;

   readonly stockTypeMap: Record<StockType, (row: TableRow) => StockTypeConfig> = {
    'bobinas': (row) => ({ value: row.stockLevel, label: 'Bobinas' }),
    '2.0': (row) => ({ value: row.stock2_0, label: 'Paquetes 2.0 TM' }),
    '2.5': (row) => ({ value: row.stock2_5, label: 'Paquetes 2.5 TM' }),
    '3.0': (row) => ({ value: row.stock3_0, label: 'Paquetes 3.0 TM' })
  };

  constructor(
     readonly quickStockAdapter: QuickStockAdapterImpl,
     readonly launchDialogService: LaunchDialogService,
     readonly cdr: ChangeDetectorRef,
     private globalMessagesService: GlobalMessageService
  ) {}

  ngOnInit(): void {
    this.initializeQuickStock();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCategorySelect(code: string): void {
    this.selectedCode = code;
    this.selectedGroup = 'Galvanized';
    this.tableDataTubes = { productList: [] };
    this.updateTableDataTubes();
  }

  onProductTypeSelect(productType: string): void {
    this.selectedGroup = productType;
    this.updateTableDataTubes();
  }

  getProductsPosition(subCategoryCode?: string): void {
    if (!subCategoryCode) return;

    this.loading = true;
    this.selectedSubCategory = subCategoryCode;

    this.quickStockAdapter.getQuickStockProduct(this.selectedCategory, subCategoryCode)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.globalMessagesService.add({
              key: 'quickstock.errors.subcategories',
            },
            GlobalMessageType.MSG_TYPE_ERROR
          );
          return throwError(() => error);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (data: Grade) => {
          this.tableData = this.formatTableData(data);
          this.processRowspans();
        },
        error: (error) => {
          console.error('Error processing category data:', error);
        }
      });
  }

  getCategoryPosition(categoryCode?: string): void {
    if (!categoryCode) return;

    this.selectedCategory = categoryCode;
    this.loading = true;
    this.subcategories = [];
    this.tableData = [];

    this.quickStockAdapter.getQuickStockCategory(categoryCode)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.globalMessagesService.add({
            key: 'quickstock.errors.categories',
          },
            GlobalMessageType.MSG_TYPE_ERROR
          );
          return throwError(() => error);
        }),
        finalize(() => {
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (data: QuickStockData) => {
          if (data.groupList.length > 0) {
            const sortedGradeSet = [...data.groupList[0].gradeSet].sort((a, b) =>
              a.gradeName.localeCompare(b.gradeName)
            );

            this.subcategories = sortedGradeSet;

            if (sortedGradeSet.length > 0) {
              this.getProductsPosition(sortedGradeSet[0].code);
            } else {
              this.loading = false;
              this.tableData = [];
            }
          } else {
            this.subcategories = [];
            this.tableData = [];
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Error processing category data:', error);
        }
      });
  }

  onStockClick(row: TableRow, stockType: StockType): void {
    const stockConfig = this.stockTypeMap[stockType](row);

    if (stockConfig.value <= 0) return;

    const dialogData: AddToCartDialogData = {
      thickness: row.thickness,
      width: row.width,
      length: row.length,
      stockType: stockConfig.label,
      stockValue: stockConfig.value
    };

    const dialog = this.launchDialogService.openDialog(
      'GONVARRI_ADDED_TO_CART',
      undefined,
      undefined,
      dialogData
    );

    dialog?.pipe(take(1)).subscribe();
  }

   initializeQuickStock(): void {
    if (isAsturias()) {
      this.initializeAsturiasStore();
    } else {
      this.initializeDefaultStore();
    }
  }

   initializeAsturiasStore(): void {
    this.selectedCode = this.ASTURIAS_DEFAULT_CODE;
    this.selectedGroup = this.ASTURIAS_DEFAULT_GROUP;
    this.fetchAsturiasData();
  }

  initializeDefaultStore(): void {
    this.loading = true;
    this.quickStockAdapter.getQuickStockTabs()
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error fetching quick stock tabs:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((data) => {
        if (data.subcategories) {

          const getOrder = (category: string): number => {
            if (isPortugal()) {
              switch (category) {
                case "groupSteel_N": return 7;
                case "groupSteel_C": return 6;
                case "groupSteel_L": return 5;
                case "groupSteel_G": return 4;
                case "groupSteel_E": return 3;
                case "groupSteel_D": return 2;
                case "groupSteel_B": return 1;
                default: return 0;
              }
            } else {
              switch (category) {
                case "groupSteel_B": return 7;
                case "groupSteel_C": return 6;
                case "groupSteel_L": return 5;
                case "groupSteel_G": return 4;
                case "groupSteel_E": return 3;
                case "groupSteel_D": return 2;
                case "groupSteel_N": return 1;
                default: return 0;
              }
            }
          };

          data.subcategories.sort((a, b) => getOrder(b.id ?? '') - getOrder(a.id ?? ''));
        }

        this.quickStockData.next(data);

        if (data.subcategories?.[0]?.id) {
          this.getCategoryPosition(data.subcategories[0].id);
        }
      });
  }

   fetchAsturiasData(): void {
    this.loading = true;
    this.quickStockAdapter.getQuickStockAsturias(this.selectedCode, this.selectedGroup)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error fetching Asturias data:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((response) => {
        console.log(response);
        this.tableDataTubes = {
          productList: this.fillRowspan(this.sortProductsByThickness(response.productList || []))
        };
        this.processRowspans();
      });
  }

   updateTableDataTubes(): void {
    this.loading = true;

    this.quickStockAdapter.getQuickStockAsturias(this.selectedCode, this.selectedGroup)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error updating table data:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((response) => {
        console.log(response);
        this.tableDataTubes = {
          productList: this.fillRowspan(this.sortProductsByThickness(response.productList || []))
        };
      });
  }

   sortProductsByThickness(products: ProductQuickStockData[]): ProductQuickStockData[] {
    return products.sort((a, b) => {
      const thicknessA = parseFloat(a.thickness.replace(',', '.'));
      const thicknessB = parseFloat(b.thickness.replace(',', '.'));
      return thicknessA - thicknessB;
    });
  }

   fillRowspan(products: ProductQuickStockData[]): ProductQuickStockData[] {
    let curThickness = "0";
    let curIndex = 0;
    const newProducts: ProductQuickStockData[] = [];

    products.forEach((product, i) => {
      const newProduct = {...product};
      if(!newProduct.thickness.includes(curThickness)) {
        curThickness = newProduct.thickness;
        curIndex = i;
        newProduct.rowspan = 1;
      }else {
        newProducts[curIndex].rowspan++;
        newProduct.rowspan = 0;
      }
      newProducts.push(newProduct);
    });
    return newProducts;
  }

   formatTableData(data: Grade): TableRow[] {
    try {
      const tableData: TableRow[] = data.thicknessSet.flatMap(thickness =>
        thickness.widthSet.flatMap(width =>
          width.lengthSet.map(length => ({
            thickness: thickness.thickness,
            width: width.width,
            length: length.length,
            stockLevel: width.stockKg || 0,
            stock1_5: length.stock1_5 || 0,
            stock2_0: length.stock2_0 || 0,
            stock2_5: length.stock2_5 || 0,
            stock3_0: length.stock3_0 || 0,
            showCell: true,
            rowspan: 1,
            stockMessage: width.stockMessage,
            formattedPrice1_5: width.formattedPrice,
            formattedPrice2_0: '€'+this.formatter.format(length.product2_0?.price?.value as number).replaceAll("EUR", "").trim(),
            formattedPrice2_5: '€'+this.formatter.format(length.product2_5?.price?.value as number).replaceAll("EUR", "").trim(),
            formattedPrice3_0: '€'+this.formatter.format(length.product3_0?.price?.value as number).replaceAll("EUR", "").trim(),
            product_default: width.productKg,
            product2_0: length.product2_0,
            product2_5: length.product2_5,
            product3_0: length.product3_0
          }))
        )
      )
      return tableData.sort((a, b) => {
        const thicknessA = parseFloat(a.thickness.replaceAll(",", "."));
        const thicknessB = parseFloat(b.thickness.replaceAll(",", "."));

        if (thicknessA !== thicknessB) {
          return thicknessA - thicknessB;
        }

        const anchoA = parseFloat(a.width.replaceAll(",", "."));
        const anchoB = parseFloat(b.width.replaceAll(",", "."));

        return anchoA - anchoB;
      });
    } catch (error) {
      console.error('Error formatting table data:', error);
      return [];
    }
  }

  protected openQuickStockModal(
    product: ProductQuickStockData | GonvarriProduct | undefined,
    lenght?: string,
    weight?: string,
    width?: string,
    thickness?: string,
    packProductRowMaxStockQuantity?: number
  ): void {
    const addToCartData = this.generateEventDataObject(
      product as GonvarriProduct,
      lenght,
      weight,
      width,
      thickness,
      packProductRowMaxStockQuantity
    );
    const dialog = this.launchDialogService.openDialog(
      "GONVARRI_ADDED_TO_CART",
      undefined,
      undefined,
      addToCartData
    );
    if (dialog) {
      dialog.pipe(take(1)).subscribe();
    }
  }

  openNotificationModal(product?: GonvarriProduct): void {
    const addToCartData = this.generateStockNotificationEventData(
      product as GonvarriProduct
    );
    const dialog = this.launchDialogService.openDialog(
      "GONVARRI_QUICK_STOCK_NOTIFICATION",
      undefined,
      undefined,
      addToCartData
    );
    if (dialog) {
      dialog.pipe(take(1)).subscribe();
    }
  }

  generateStockNotificationEventData(product: GonvarriProduct, channel?: string): GonvarriQuickStockNotificationEvent {
    const quickStockNotificationEvent = new GonvarriQuickStockNotificationEvent();
    quickStockNotificationEvent.productCode = product.code as string;
    quickStockNotificationEvent.channel = channel;
    return quickStockNotificationEvent;
  }

   generateEventDataObject(product: ProductQuickStockData | GonvarriProduct, lenght?: string, weight?: string, width?: string, thickness?: string, packProductRowMaxStockQuantity?: number): GonvarriCartUiEventAddToCart {
    const cartUiData = new GonvarriCartUiEventAddToCart();
    cartUiData.quantity = 1;
    cartUiData.product = (product as any).fullProduct !== undefined ? ((product as ProductQuickStockData).fullProduct as GonvarriProduct) : product as GonvarriProduct;
    cartUiData.numberOfEntriesBeforeAdd = 0;
    cartUiData.pickupStoreName = undefined;
    cartUiData.lenght = lenght;
    cartUiData.weight = weight;
    cartUiData.disableInputs = weight !== undefined;
    cartUiData.width = width;
    cartUiData.thickness = thickness;
    cartUiData.packProductRowMaxStockQuantity = packProductRowMaxStockQuantity;
    return cartUiData;
  }

   processRowspans(): void {
    let currentThickness = '';
    let count = 1;
    let startIndex = 0;

    try {
      this.tableData.forEach((row, index) => {
        if (currentThickness === row.thickness) {
          count++;
          row.showCell = false;
        } else {
          if (count > 1) {
            this.tableData[startIndex].rowspan = count;
          }
          currentThickness = row.thickness;
          count = 1;
          startIndex = index;
          row.showCell = true;
        }
      });

      if (count > 1) {
        this.tableData[startIndex].rowspan = count;
      }
    } catch (error) {
      console.error('Error processing rowspans:', error);
    }
  }

  formatStockLevel(stockLevel: number): string {
    let stockLevelFormated = stockLevel.toString();
    if(stockLevelFormated.length > 3) {
      const point = stockLevelFormated.length > 4 ? 2 : 1;
      const stockLevelStart = stockLevelFormated.substring(0, point);
      const stockLevelEnd = stockLevelFormated.substring(point, stockLevelFormated.length);
      stockLevelFormated = stockLevelStart + "." + stockLevelEnd;
    }
    return stockLevelFormated;
  }

  getGroupName(group: string): string {
    return group === 'G' ? 'Galvanizado' :
           group === 'N' ? 'Negro' :
           group === 'D' ? 'Decapado' : '';
  }

  protected readonly isAsturias = isAsturias;
}
